import { GOOGLE_MAPS_API_KEY } from "../googleMapsConfig";
import React, { useRef, useState, useEffect } from "react";
import { Modal, Button } from "antd";
import {
  GoogleMap,
  Marker,
  useJsApiLoader,
  Autocomplete,
} from "@react-google-maps/api";

const libraries = ["places"];
const containerStyle = {
  width: "100%",
  height: "300px",
};

const defaultCenter = {
  lat: -1.286389,
  lng: 36.817223,
};

const isFiniteCoord = (val) => typeof val === "number" && Number.isFinite(val);

const LocationPicker = ({ visible, onClose, onSelect, initialLocation }) => {
  const [map, setMap] = useState(null);
  const [markerPos, setMarkerPos] = useState(defaultCenter);
  const [placeName, setPlaceName] = useState("");
  const autocompleteRef = useRef(null);

  const normalizeCenter = (center) => {
    if (
      center &&
      Array.isArray(center) &&
      isFiniteCoord(center[0]) &&
      isFiniteCoord(center[1])
    ) {
      return { lng: center[0], lat: center[1] };
    }
    return null;
  };

  // Initialize with initialLocation if provided
  useEffect(() => {
    if (visible && initialLocation) {
      const normalized = normalizeCenter(initialLocation.center);
      if (normalized) {
        setMarkerPos({ lat: normalized.lat, lng: normalized.lng });
        if (map) {
          map.panTo({ lat: normalized.lat, lng: normalized.lng });
        }
      } else if (initialLocation.place_name && window.google?.maps?.Geocoder) {
        // If we only have place_name, geocode it to get coordinates
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode(
          { address: initialLocation.place_name },
          (results, status) => {
            if (status === "OK" && results[0]) {
              const location = results[0].geometry.location;
              const latLng = {
                lat: location.lat(),
                lng: location.lng(),
              };
              setMarkerPos(latLng);
              if (map) {
                map.panTo(latLng);
              }
            }
          }
        );
      }
      if (initialLocation.place_name) {
        setPlaceName(initialLocation.place_name);
      }
    } else if (visible && !initialLocation) {
      // Reset to default when opening without initial location
      setMarkerPos(defaultCenter);
      setPlaceName("");
    }
  }, [visible, initialLocation, map]);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const onPlaceChanged = () => {
    const place = autocompleteRef.current?.getPlace?.();
    if (place?.geometry) {
      const location = place.geometry.location;
      const latLng = {
        lat: location.lat(),
        lng: location.lng(),
      };
      setMarkerPos(latLng);
      map?.panTo(latLng);
      setPlaceName(place.formatted_address);
    }
  };

  const handleDragEnd = async (e) => {
    const lat = e.latLng.lat();
    const lng = e.latLng.lng();
    setMarkerPos({ lat, lng });

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results[0]) {
        setPlaceName(results[0].formatted_address);
      }
    });
  };

  const handleSelect = () => {
    onSelect({
      place_name: placeName,
      center: [markerPos.lng, markerPos.lat],
    });
    clearSelection();
  };

  const clearSelection = () => {
    setMarkerPos(defaultCenter);
    setPlaceName("");
  };

  if (!isLoaded) return null;

  return (
    <Modal
      title="Location"
      open={visible}
      onCancel={() => {
        onClose();
        clearSelection();
      }}
      footer={null}
    >
      <Autocomplete
        onLoad={(ref) => (autocompleteRef.current = ref)}
        onPlaceChanged={onPlaceChanged}
      >
        <input
          type="text"
          placeholder="Search location..."
          style={{ width: "100%", padding: "10px", marginBottom: "10px" }}
          value={placeName}
          onChange={(e) => setPlaceName(e.target.value)}
        />
      </Autocomplete>

      <GoogleMap
        mapContainerStyle={containerStyle}
        center={markerPos}
        zoom={20}
        onLoad={(map) => {
          setMap(map);
          // If we have initial location, pan to it after map loads
          const normalized = normalizeCenter(initialLocation?.center);
          if (normalized) {
            map.panTo({ lat: normalized.lat, lng: normalized.lng });
          }
        }}
      >
        <Marker position={markerPos} draggable onDragEnd={handleDragEnd} />
      </GoogleMap>

      <div style={{ marginTop: "10px", textAlign: "right" }}>
        <Button
          onClick={() => {
            onClose();
            clearSelection();
          }}
          style={{ marginRight: "10px" }}
        >
          Cancel
        </Button>
        <Button type="primary" onClick={handleSelect} disabled={!placeName}>
          Select
        </Button>
      </div>
    </Modal>
  );
};

export default LocationPicker;
